import React, { useEffect, useMemo, useRef, useState } from "react"
import { createFilterOptions, Box, IconButton, List, ListItem, Paper, Popper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, useAutocomplete, useTheme, Typography, Popover, Button, SvgIcon, ClickAwayListener, CircularProgress } from "@mui/material";
import { useParams } from "react-router-dom";
import { t } from "i18next";
import { ICurrencyAutocompleteGuiModel } from "../../../model/ICurrencyGuiModel";
import { IAmountGuiModel } from "../../../model/ISourceDocumentGuiModel";
import CurrencyBusinessLogic from "../../../businessLogic/CurrencyBusinessLogic";
interface AmountProps {
    amountObj: IAmountGuiModel
    handleUpdateCurrencyId: (id: number | undefined, error: boolean) => void,
}

const filterOptions = createFilterOptions({
    matchFrom: "any",
    limit: 100,
    ignoreCase: true,
    stringify: (options: ICurrencyAutocompleteGuiModel) => options.name + " " + options.currencyCodeId
});

const AutocompleteCurrencies = (props: AmountProps) => {

    const theme = useTheme();

    const {
        amountObj,
        handleUpdateCurrencyId,} = props;

    const highligthedOption = useRef<ICurrencyAutocompleteGuiModel | null>(null);

    const { organizationId } = useParams();

    const [currencyList, setCurrencyList] = useState<ICurrencyAutocompleteGuiModel[]>([]);
    const [currencyListError, setCurrencyListError] = useState<string[]>([]);

    const selectedCurrency = useMemo(() => {
        let newValue = currencyList.find(e => e.currencyCodeId === amountObj.currency?.currencyVismaId)
        if (newValue) {
            return newValue
        } else {return null }

    },[amountObj, currencyList]);

    const [loader, setLoader] = useState(false);

    useEffect(() => {

        let isMounted = true; //Avoid memory leaks. Async function running after dismount.

        const getCurrencies = async () => {

            let logic = new CurrencyBusinessLogic();
            setLoader(true);
            const result = await logic.getCurrenciesAutocomplete(organizationId ? organizationId : "");
            setLoader(false);
            if (result.hasErrors) {
                if (isMounted) setCurrencyListError(result.errors);
            } else {
                if (isMounted) setCurrencyListError([]);
                if (isMounted) {
                    setCurrencyList(result.value);
                }
            }
        }
        getCurrencies();

        return () => { isMounted = false };

    }, [organizationId]);

    const {
        getRootProps,
        getInputProps,
        getListboxProps,
        getOptionProps,
        groupedOptions,
        inputValue,
    } = useAutocomplete({
        id: 'currency-autocomplete',
        options: currencyList,
        value: selectedCurrency,
        isOptionEqualToValue: (option, value) => value && option.currencyCodeId === value.currencyCodeId ? true : false,

        onChange: (event: any, newValue: ICurrencyAutocompleteGuiModel | null) => handleUpdateCurrencyId(newValue?.currencyCodeId, false),

        onHighlightChange: (event, option, reason) => {
            highligthedOption.current = option;
        },
        filterOptions: filterOptions,
        selectOnFocus: true,
        autoSelect: true,
        openOnFocus: true,
        onClose: (event) => {
            setAnchorEl(null);
            let newValue;
            if (!highligthedOption.current) {
                let newValueFromInput: ICurrencyAutocompleteGuiModel | undefined;
                if ((newValueFromInput = currencyList.find(e => e.currencyCodeId.toString() === inputValue))) {
                    newValue = newValueFromInput;
                } else if (inputValue === "") {
                    newValue = null;
                } else {
                    newValue = null;
                }
                handleUpdateCurrencyId(newValue?.currencyCodeId, false);
            }
        },
        getOptionLabel: (option) => option.currencyCodeId + " - " + option.name,
    });

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    return loader || selectedCurrency === undefined ?
        <Stack direction="row">
            <Typography>{amountObj.currency?.currencyVismaId}</Typography>
            <CircularProgress />
        </Stack>
        : < div {...getRootProps()}>
        <TextField
            label={t('basic.currencyNo')}
            size="small"
            variant="filled"
            error={amountObj.currency && amountObj.currency.currencyVismaId ? false : true}
            onFocus={(e) => setAnchorEl(e.currentTarget)}

            inputProps={{ ...getInputProps() }}
            InputProps={{ disableUnderline: true }}
            sx={{
                width: "11rem", "& .MuiInputBase-root": { height: "2.5rem" },
                border: amountObj.currency && amountObj.currency.currencyVismaId ? 0 : 2,
                borderColor: "red"
            }}
        />
        <Popper sx={{ zIndex: 2001, }} open={Boolean(anchorEl)} anchorEl={anchorEl} placement="bottom-start" onMouseDown={event => event.preventDefault()}>
            <Box component={Paper} sx={{ border: 1, borderColor: 'divider' }} elevation={10}>
                <Stack direction="row" justifyContent="space-between" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="button">{t("basic.otherCurrency")}</Typography>
                </Stack>
                <TableContainer>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: "2rem" }}>{t("basic.id")}</TableCell>
                                <TableCell sx={{ width: "10rem" }}>{t("basic.name")}</TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </TableContainer>
                <TableContainer component={List} {...getListboxProps()} disablePadding
                    sx={{ maxHeight: "15rem", }}>
                    <Table size="small">
                        <TableBody role="listbox">
                            {(groupedOptions as typeof currencyList).map((option, index) => (
                                <TableRow component={ListItem} {...getOptionProps({ option, index })} key={index} sx={{ zIndex: theme.zIndex.appBar + 1 }}>
                                    <TableCell sx={{ width: "2rem" }}>
                                        {option.currencyCodeId}
                                    </TableCell>
                                    <TableCell sx={{ width: "10rem" }}>
                                        {option.name}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Popper>
    </div>
}
export default AutocompleteCurrencies;